/**
 * CallWorkspaceContext — open/minimized state for the full-screen "Call Mode"
 * workspace. Kept SEPARATE from SoftphoneContext on purpose: that context is
 * call-transport state, deliberately userId-keyed and stable so an auth refresh
 * never re-renders mid-call (the ~90s call-drop guard). UI open/close churn does
 * not belong there.
 *
 * Auto-opens ONCE per call the moment it is INITIATED (dialing/ringing, not only
 * once the lead answers) when we know which contact we're calling — outbound
 * calls carry active.contactId from the first render of startCall. Inbound calls
 * carry no contactId on the live call, so the incoming-call popup instead pins
 * the matched lead explicitly via openWorkspace(contactId) when the recruiter
 * clicks the popup / answers.
 *
 * The workspace does NOT auto-close when the call ends: the recruiter keeps it up
 * to finish notes / set a disposition and dismisses it themselves via close().
 * Because SoftphoneContext.endLocal nulls `active` the instant the line drops, we
 * PIN the contact id here so the panel (and its contactId-keyed state — detail,
 * studies, prescreen session, notes draft) survives the hang-up until the
 * recruiter closes it. Starting a NEW call re-points the workspace onto the new
 * contact.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useSoftphone } from './SoftphoneContext';

interface CallWorkspaceValue {
  open: boolean;
  minimized: boolean;
  /** The contact the workspace is bound to — PINNED, so it survives call-end. */
  activeContactId: string | null;
  /** True once the live call has ended but the workspace is still open. */
  callEnded: boolean;
  /** Manually open the workspace, optionally pinning a matched contact — the
   *  inbound "match caller → open" flow (the live call carries no contactId). */
  openWorkspace: (contactId?: string) => void;
  minimize: () => void;
  restore: () => void;
  /** Fully dismiss the workspace — does NOT hang up a live call. */
  close: () => void;
}

const CallWorkspaceContext = createContext<CallWorkspaceValue | null>(null);

export function useCallWorkspace(): CallWorkspaceValue {
  const ctx = useContext(CallWorkspaceContext);
  if (!ctx) throw new Error('useCallWorkspace must be used within CallWorkspaceProvider');
  return ctx;
}

export function CallWorkspaceProvider({ children }: { children: ReactNode }) {
  const { callStatus, active } = useSoftphone();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // The contact bound to the workspace. Pinned (kept after the call ends) so the
  // panel doesn't unmount the moment endLocal nulls `active`.
  const [pinnedContactId, setPinnedContactId] = useState<string | null>(null);
  // Guards a single auto-open per call (keyed on the contact) so re-opening after
  // a manual close/minimize doesn't fight the recruiter on the next render.
  const autoOpenedFor = useRef<string | null>(null);

  const liveContactId = active?.contactId || null;

  useEffect(() => {
    // Any in-flight call (connecting / ringing / open) to a known contact opens
    // Call Mode immediately — startCall sets active.contactId synchronously
    // BEFORE callStatus flips to 'connecting', so this fires on dial, not answer.
    if (callStatus !== 'idle' && liveContactId) {
      setPinnedContactId(liveContactId);
      if (autoOpenedFor.current !== liveContactId) {
        autoOpenedFor.current = liveContactId;
        setOpen(true);
        setMinimized(false);
      }
    } else if (callStatus === 'idle') {
      // Call ended: deliberately DON'T close — the recruiter dismisses it. Just
      // clear the auto-open guard so the NEXT call can re-open the workspace.
      autoOpenedFor.current = null;
    }
  }, [callStatus, liveContactId]);

  const openWorkspace = useCallback((contactId?: string) => {
    if (contactId) {
      // Inbound match-caller open: the live call has no contactId, so pin the
      // matched lead here directly. Mark it as already auto-opened so the
      // call-status effect / close-guard don't fight this manual open.
      setPinnedContactId(contactId);
      autoOpenedFor.current = contactId;
    }
    setOpen(true);
    setMinimized(false);
  }, []);
  const minimize = useCallback(() => setMinimized(true), []);
  const restore = useCallback(() => setMinimized(false), []);
  const close = useCallback(() => {
    setOpen(false);
    setMinimized(false);
    setPinnedContactId(null);
    // Pin the guard to the LIVE call (not null) so closing mid-dial
    // (connecting/ringing) isn't undone by the next status tick re-opening it.
    // The idle branch above resets the guard when the call ends, so the NEXT
    // call still auto-opens.
    autoOpenedFor.current = liveContactId;
  }, [liveContactId]);

  const callEnded = callStatus === 'idle';

  return (
    <CallWorkspaceContext.Provider
      value={{ open, minimized, activeContactId: pinnedContactId, callEnded, openWorkspace, minimize, restore, close }}
    >
      {children}
    </CallWorkspaceContext.Provider>
  );
}

export default CallWorkspaceProvider;
